import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PackMetadataInputT } from '@skey/manifest';
import PackMetadataForm from '../components/PackMetadataForm';
import AssetUploader from '../components/AssetUploader';
import AssetGrid from '../components/AssetGrid';
import PublishBar from '../components/PublishBar';
import Sticker9Cropper from '../components/StickerCropper';
import { useToastStore } from '../components/Toast';
import { listAssets, deleteAsset, reorderAssets } from '../lib/assets';
import {
  getPack,
  patchPack,
  publishPack,
  unpublishPack,
} from '../lib/packs';
import { useUploadStore } from '../lib/uploadStore';

/**
 * `/packs/:id` — pack editor.
 *
 * Layout (top to bottom):
 *   1. Sticky header with breadcrumb + pack name.
 *   2. Sticky publish bar (Requirement 23.1, 23.4).
 *   3. Metadata form (Requirement 22.1).
 *   4. Asset uploader (Requirement 22.2).
 *   5. Sortable asset grid (Requirement 22.3, 22.4, 24.x).
 *
 * Data flow:
 *   - One `useQuery` per resource (pack detail + assets) so the cache
 *     keys stay simple and we can invalidate them surgically after
 *     mutations.
 *   - All mutations use TanStack Query's `useMutation` so the loading
 *     state is centralised; on success they invalidate the relevant
 *     query and push a Vietnamese toast (Requirement 25.1).
 *   - 404 on the pack query bounces back to `/packs` because it means
 *     the admin opened a stale link.
 *
 * Validates Requirements 22.1, 22.2, 22.3, 22.4, 23.1, 23.2, 23.3,
 * 23.4, 24.1, 24.2, 25.1.
 */

export default function PackEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const resetUploadQueue = useUploadStore((s) => s.reset);
  const clearDoneUploads = useUploadStore((s) => s.clearDone);

  // Drop the upload-queue rows when the admin navigates away or switches
  // packs — they reference a different pack id and would be confusing
  // on a sibling editor.
  useEffect(() => {
    return () => {
      resetUploadQueue();
    };
  }, [id, resetUploadQueue]);

  const [showCropper, setShowCropper] = useState(false);

  const packQuery = useQuery({
    queryKey: ['pack', id],
    queryFn: () => getPack(id!),
    enabled: !!id,
  });

  const assetsQuery = useQuery({
    queryKey: ['pack-assets', id],
    queryFn: () => listAssets(id!),
    enabled: !!id,
  });

  // ------------------------------------------------------------------
  // Mutations
  // ------------------------------------------------------------------

  const patchMutation = useMutation({
    mutationFn: (values: PackMetadataInputT) => {
      return patchPack(id!, {
        name: values.name,
        description:
          values.description !== undefined && values.description.length > 0
            ? values.description
            : null,
        priceVnd: values.priceVnd ?? 0,
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['pack', id], updated);
      addToast({
        severity: 'success',
        title: 'Đã lưu',
        message: 'Đã lưu thay đổi pack',
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (order: Array<{ assetId: string; position: number }>) =>
      reorderAssets(id!, order),
    onSuccess: (items) => {
      queryClient.setQueryData(['pack-assets', id], items);
      addToast({
        severity: 'success',
        title: 'Đã lưu',
        message: 'Đã lưu thứ tự asset',
      });
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: (assetId: string) => deleteAsset(id!, assetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pack-assets', id] });
      addToast({
        severity: 'success',
        title: 'Đã xoá',
        message: 'Đã xoá asset',
      });
    },
  });

  const setCoverMutation = useMutation({
    mutationFn: (assetId: string) =>
      patchPack(id!, { coverAssetId: assetId }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['pack', id], updated);
      addToast({
        severity: 'success',
        title: 'Đã đặt bìa',
        message: 'Đã cập nhật ảnh bìa của pack',
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => publishPack(id!),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['pack', id] });
      void queryClient.invalidateQueries({ queryKey: ['pack-assets', id] });
      void queryClient.invalidateQueries({ queryKey: ['packs'] });
      addToast({
        severity: 'success',
        title: 'Đã phát hành',
        message: `Đã phát hành phiên bản v${response.version}`,
      });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishPack(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pack', id] });
      void queryClient.invalidateQueries({ queryKey: ['packs'] });
      addToast({
        severity: 'success',
        title: 'Đã gỡ phát hành',
        message: 'Pack đã được gỡ khỏi catalog',
      });
    },
  });

  if (!id) {
    return <Navigate />;
  }

  if (packQuery.isLoading) {
    return <LoadingState />;
  }

  if (packQuery.isError) {
    return (
      <ErrorState
        message={packQuery.error?.message ?? 'Không tải được pack'}
        onBack={() => navigate('/packs')}
      />
    );
  }

  const pack = packQuery.data;
  const assets = assetsQuery.data ?? [];
  if (!pack) {
    return (
      <ErrorState
        message="Không tìm thấy pack"
        onBack={() => navigate('/packs')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/packs"
              className="text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              ← Danh sách pack
            </Link>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              {pack.name}
            </h1>
            <p className="text-xs text-slate-500">
              <code className="font-mono">{pack.id}</code>
            </p>
          </div>
        </header>

        {/* Sticky metadata + publish bar. Stacks vertically because the
            metadata form is too tall to share a row on most screens. */}
        <div className="sticky top-0 z-10 -mx-6 mb-6 space-y-3 border-b border-slate-200 bg-slate-50/95 px-6 py-3 backdrop-blur">
          <PublishBar
            pack={pack}
            assets={assets}
            isPublishing={publishMutation.isPending}
            isUnpublishing={unpublishMutation.isPending}
            onPublish={async () => {
              await publishMutation.mutateAsync();
            }}
            onUnpublish={async () => {
              await unpublishMutation.mutateAsync();
            }}
          />
        </div>

        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-slate-900">
            Thông tin pack
          </h2>
          <PackMetadataForm
            initialValues={{
              name: pack.name,
              description: pack.description ?? '',
              kind: pack.kind,
              priceVnd: pack.price_vnd,
              tags: [],
            }}
            lockKind
            disabled={patchMutation.isPending}
            submitLabel="Lưu"
            onSubmit={async (values) => {
              await patchMutation.mutateAsync(values);
            }}
          />
        </section>

        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <AssetUploader
            packId={pack.id}
            packKind={pack.kind}
            currentAssetCount={assets.length}
            disabled={pack.pack_state !== 'draft'}
            onUploaded={() => {
              void queryClient.invalidateQueries({
                queryKey: ['pack-assets', id],
              });
              // Drop "done" rows once we've refetched, so the queue
              // empties out gracefully.
              window.setTimeout(() => clearDoneUploads(), 1500);
            }}
          />
          {pack.pack_state !== 'draft' ? (
            <p className="mt-2 text-xs text-amber-700">
              Chuyển pack về trạng thái nháp trước khi tải thêm asset.
            </p>
          ) : null}

          {/* 9-up sticker cropper. Sticker packs only — GIF packs don't
              benefit from a square-grid splitter. */}
          {pack.kind === 'sticker' && pack.pack_state === 'draft' ? (
            <div className="mt-4 flex items-center justify-between rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  Có ảnh tổng hợp 3×3?
                </p>
                <p className="text-xs text-slate-500">
                  Cắt ảnh thành 9 sticker rời và tải tất cả lên trong một lần.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCropper(true)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100"
              >
                Mở công cụ cắt
              </button>
            </div>
          ) : null}
        </section>

        <Sticker9Cropper
          open={showCropper}
          onClose={() => setShowCropper(false)}
          packId={pack.id}
          onUploaded={() => {
            void queryClient.invalidateQueries({ queryKey: ['pack-assets', id] });
          }}
        />

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <AssetGrid
            packId={pack.id}
            packKind={pack.kind}
            packState={pack.pack_state}
            assets={assets}
            coverAssetId={pack.cover_asset_id}
            isSavingOrder={reorderMutation.isPending}
            isDeleting={deleteAssetMutation.isPending}
            isSettingCover={setCoverMutation.isPending}
            onSaveOrder={async (order) => {
              await reorderMutation.mutateAsync(order);
            }}
            onDeleteAsset={async (assetId) => {
              await deleteAssetMutation.mutateAsync(assetId);
            }}
            onSetCover={async (assetId) => {
              await setCoverMutation.mutateAsync(assetId);
            }}
          />
        </section>
      </div>
    </div>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div
          role="status"
          aria-label="Đang tải pack"
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="h-6 w-1/3 animate-pulse rounded bg-slate-100" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
          <div className="h-32 w-full animate-pulse rounded bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

function ErrorState({
  message,
  onBack,
}: {
  message: string;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
        >
          <p className="font-medium">{message}</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-3 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-100"
          >
            Về danh sách pack
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tiny helper to redirect to /packs without writing JSX; only used in the
 * "no id" branch which should be unreachable thanks to React Router.
 */
function Navigate(): JSX.Element {
  // Defensive fallback: when the id param is somehow missing, send the
  // admin to the list rather than render a broken page.
  if (typeof window !== 'undefined') {
    window.location.replace('/packs');
  }
  return <></>;
}
